import './pdf.scss'
import html2pdf from 'html2pdf.js'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Fab,
  FormControlLabel,
  Paper,
  Typography,
} from '@mui/material'
import { useEffect, useMemo, useState, useRef } from 'react'
import { buildTimeStringFromTimestamp } from '../../../components/common/time/timeHelper'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { useFindReportByKybQuery } from '../../../services/guenoApi'
import CheckJap from './KybToPdf/checks/CheckDetailJap'
import CheckSerasa from './KybToPdf/checks/CheckSerasaDetail'
import CheckNegativeMedia from './KybToPdf/checks/CheckNegativeMedia'
import CheckKyc from './KybToPdf/checks/CheckKycDetail'
import CheckCade from './KybToPdf/checks/CheckCadeDetail'
import CheckDomains from './KybToPdf/checks/CheckDetailDomains'
import CheckCdn from './KybToPdf/checks/CheckCdnDetail'
import CheckDebtor from './KybToPdf/checks/CheckDebtorsDetail'
import CheckLawSuits from './KybToPdf/checks/CheckLawSuitDetail'
import CheckCnep from './KybToPdf/checks/CheckCnepDetail'
import CheckCeaf from './KybToPdf/checks/CheckCeafDetail'
import CheckCepim from './KybToPdf/checks/CheckCepimDetail'
import CheckProtestos from './KybToPdf/checks/CheckProtestos'
import CheckLeniencyAgreements from './KybToPdf/checks/CheckLeniencyAgreement'
import CheckComprot from './KybToPdf/checks/CheckComprot'
import CheckChequesRechazados from './KybToPdf/checks/CheckChequesRechazados'
import CheckSujetosObligados from './KybToPdf/checks/CheckSujetosObligados'
import CheckDeudores from './KybToPdf/checks/CheckDeudores'
import CheckRepet from './KybToPdf/checks/CheckRepet'
import CheckBlacklists from './KybToPdf/checks/CheckBlacklists'
import CheckRenaper from './KybToPdf/checks/CheckRenaper'
import CheckCreditRisk from './KybToPdf/checks/CheckCreditRisk'
import CheckSintegra from './KybToPdf/checks/CheckSintegra'
import Cover from './KybToPdf/Cover'
import kybChecks from './KybChecks.json'
import kybChecksDefaultOrder from './KybChecksDefaultOrder.json'
import CompanyDetails from './KybToPdf/checks/CompanyDetails'
import Consultations from './KybToPdf/checks/Consultations'
import ExecutiveSummary from './KybToPdf/checks/ExecutiveSummary'
import ManualInfo from './KybToPdf/checks/ManualInfo'
import RiskMatrixResults from './KybToPdf/checks/RiskMatrixResults'
import CheckCeis from './KybToPdf/checks/CheckCeisDetail'
import CheckShareholders from './KybToPdf/checks/CheckShareholders'
import CheckAssignedUsers from './KybToPdf/checks/CheckAssignedUsers'
import CheckComments from './KybToPdf/checks/CheckComments'
import CheckTimeline from './KybToPdf/checks/CheckTimeline'
import CheckStatus from './KybToPdf/checks/CheckStatus'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined'
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined'
import CheckBusinessShareholders from './KybToPdf/checks/CheckBusinessShareholders'
import CheckFaturamentoPresumido from './KybToPdf/checks/CheckFaturamentoPresumido'
import CheckSocialAssistance from './KybToPdf/checks/CheckSocialAssistanceDetail'
import CheckLawSuitsPerson from './KybToPdf/checks/CheckLawSuitsPerson'
import CheckRegistrationStatus from './KybToPdf/checks/CheckRegistrationStatus'
import CheckJapJus from './KybToPdf/checks/CheckDetailJapJus'
import CheckAmldd from './KybToPdf/checks/CheckAmldd'
import { KybUtils } from '../kyb.utils'

const checkComponentMap = {
  jap: CheckJap,
  jap_jus: CheckJapJus,
  japJusBrasil: CheckJap,
  serasa: CheckSerasa,
  cnep: CheckCnep,
  cepim: CheckCepim,
  protestos: CheckProtestos,
  ceis: CheckCeis,
  leniencyAgreements: CheckLeniencyAgreements,
  comprotPerson: CheckComprot,
  comprotCompany: CheckComprot,
  debtors: CheckDebtor,
  domains: CheckDomains,
  kyc: CheckKyc,
  cdn: CheckCdn,
  cade: CheckCade,
  lawsuits: CheckLawSuits,
  chequesRechazados: CheckChequesRechazados,
  sujetosObligados: CheckSujetosObligados,
  deudores: CheckDeudores,
  repet: CheckRepet,
  blacklists: CheckBlacklists,
  renaper: CheckRenaper,
  creditRiskCompany: CheckCreditRisk,
  creditRiskPerson: CheckCreditRisk,
  sintegra: CheckSintegra,
  negativeMedia: CheckNegativeMedia,
  shareholders: CheckShareholders,
  ceaf: CheckCeaf,
  faturamentoPresumido: CheckFaturamentoPresumido,
  registrationStatus: CheckRegistrationStatus,
  socialAssistance: CheckSocialAssistance,
  lawsuitsPerson: CheckLawSuitsPerson,
  amlddEsg: CheckAmldd,
  amlddLri: CheckAmldd,
  amlddPep: CheckAmldd,
  amlddMtlsn: CheckAmldd,
  amlddMtlsnSimple: CheckAmldd,
  amlddMtlsnWithNews: CheckAmldd,
}

const checkPropMap = {
  jap: 'jap',
  jap_jus: 'jap_jus',
  japJusBrasil: 'japJusBrasil',
  serasa: 'serasaCheck',
  cnep: 'cnepCheck',
  cepim: 'cepimCheck',
  protestos: 'protestosCheck',
  ceis: 'ceisCheck',
  leniencyAgreements: 'leniencyAgreementsCheck',
  comprotPerson: 'comprotCheck',
  comprotCompany: 'comprotCheck',
  debtors: 'debtorsCheck',
  domains: 'domainCheck',
  kyc: 'kycCheck',
  cdn: 'cdnCheck',
  cade: 'cadeCheck',
  lawsuits: 'lawSuitsCheck',
  chequesRechazados: 'chequesRechazadosCheck',
  sujetosObligados: 'sujetosObligadosCheck',
  deudores: 'deudoresCheck',
  repet: 'repetCheck',
  blacklists: 'check',
  renaper: 'renaperCheck',
  creditRiskCompany: 'creditRiskCheck',
  creditRiskPerson: 'creditRiskCheck',
  sintegra: 'sintegraCheck',
  negativeMedia: 'negativeMediaCheck',
  ceaf: 'ceafCheck',
  shareholders: 'shareholdersCheck',
  faturamentoPresumido: 'check',
  registrationStatus: 'registrationStatusCheck',
  socialAssistance: 'socialAssistanceCheck',
  lawsuitsPerson: 'lawsuitsPersonCheck',
  amlddEsg: 'amlddEsgCheck',
  amlddLri: 'amlddLriCheck',
  amlddPep: 'amlddPepCheck',
  amlddMtlsn: 'amlddMtlsnCheck',
  amlddMtlsnSimple: 'amlddMtlsnSimpleCheck',
  amlddMtlsnWithNews: 'amlddMtlsnWithNewsCheck',
}

const KybToPdf = ({ kyb, entity, currentCountry, setOpen }) => {
  const { email } = useSelector((state) => state.login)
  const { theme } = useSelector((state) => state.theme)
  const { t } = useTranslation(['common', 'messages'])
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)
  const today = new Date()
  const contentRef = useRef(null)
  const isInitialMount = useRef(true)

  const [checksVisibility, setChecksVisibility] = useState({})
  const [checksData, setChecksData] = useState({})

  const toggleVisibility = (key) => {
    setChecksVisibility((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const documentNumber = kyb?.companyDetails?.documentNumber ?? kyb?.documentNumber

  const { data, isFetching, isLoading } = useFindReportByKybQuery({
    id: kyb?._id,
    currentCountry,
    entity,
    documentNumber,
  })

  const dataShareHoldersPersons = data?.data?.shareHoldersPersons || []
  const dataShareHoldersBusiness = data?.data?.shareHoldersBusiness || []
  const dataRelatedPersons = data?.data?.relatedPersons || []
  const dataRelatedCompanies = data?.data?.relatedCompanies || []

  const adaptedShareHoldersBusiness =
    entity === 'BUSINESS'
      ? dataShareHoldersBusiness.map((item) => {
          if (item.companyDetails) return item
          return {
            companyDetails: {
              ...item,
              name: item.name ?? '---',
              documentNumber: item.documentNumber ?? '---',
            },
          }
        })
      : dataShareHoldersBusiness

  const sections = useMemo(() => {
    const sectionsOrder = kybChecksDefaultOrder[currentCountry]?.[entity]
    const checksByCountry = kybChecks[currentCountry]?.[entity] || []

    let ordered = []

    if (Array.isArray(sectionsOrder) && sectionsOrder.length) {
      ordered = sectionsOrder.map((title) => checksByCountry.find((s) => s.title === title)).filter(Boolean)

      const missing = checksByCountry.filter((s) => !sectionsOrder.includes(s.title))
      ordered = [...ordered, ...missing]
    } else {
      ordered = checksByCountry
    }

    return ordered.map((section) => ({
      ...section,
      items: section.items.filter((it) => {
        // Filter JusBrasil check based on email restrictions
        if (KybUtils.isJapJusCheck(it.name) && !KybUtils.canViewCheck(it.name, email, 'jap_jus')) {
          return false
        }
        // Filter AMLDD checks based on email restrictions
        if (KybUtils.isAmlddCheck(it.name) && !KybUtils.canViewCheck(it.name, email, 'amldd')) {
          return false
        }
        return checkComponentMap[it.name]
      }),
    }))
  }, [currentCountry, entity, email])

  const titleMap = useMemo(() => {
    const map = {}
    sections.forEach((section) => {
      section.items.forEach((it) => {
        map[it.name] = it.title
      })
    })
    map.shareHoldersPersons = 'shareHoldersPersons'
    map.shareHoldersBusiness = 'shareHoldersBusiness'
    return map
  }, [sections])

  const hasRelatedPersons = useMemo(() => dataRelatedPersons.length > 0, [dataRelatedPersons.length])
  const hasRelatedCompanies = useMemo(() => dataRelatedCompanies.length > 0, [dataRelatedCompanies.length])

  useEffect(() => {
    // Only run when modal opens (kyb._id changes)
    if (!kyb?._id) return

    const newChecksData = {} // For dynamic section items
    // Initial visibility states for general sections and sections with direct data props
    const baseVisibility = {
      consultationBlock: true,
      executiveSummarySection: true,
      manualDataSection: entity === 'BUSINESS' ? true : false,
      companyDetails: true,
      riskMatrixResults: !!(kyb?.riskResult || kyb?.riskScore),
      shareHoldersPersons: entity === 'BUSINESS' ? true : false,
      shareHoldersBusiness: entity === 'BUSINESS' ? true : false,
      relatedPersons: hasRelatedPersons,
      relatedCompanies: hasRelatedCompanies,
      timeline: !!(kyb?.timeline && kyb?.timeline.length > 0),
      assignedUsers: !!(kyb?.assignedUsers && kyb?.assignedUsers.length > 0),
      comments: !!(kyb?.comments && kyb?.comments.length > 0),
      statusSection: !!kyb?.status,
    }

    // Handle dynamic checks from kyb data directly (without iterating sections)
    const dynamicChecksVisibility = {}
    // Get all check names from kyb object
    Object.keys(kyb).forEach((key) => {
      // Check if this is a check property (has checkDetails)
      if (kyb[key]?.checkDetails && checkComponentMap[key]) {
        newChecksData[key] = kyb[key]
        dynamicChecksVisibility[key] = true
      }
    })

    // Combine visibility states: base settings, then dynamic checks.
    const combinedInitialVisibility = { ...baseVisibility, ...dynamicChecksVisibility }

    setChecksData(newChecksData)

    // Only set initial visibility on first mount, then respect user toggles
    if (isInitialMount.current) {
      setChecksVisibility(combinedInitialVisibility)
      isInitialMount.current = false
    } else {
      // On subsequent renders, only update if there are actually new keys
      setChecksVisibility((prev) => {
        // Check if there are any NEW keys that don't exist in prev
        const newKeys = Object.keys(combinedInitialVisibility).filter(key => !(key in prev))

        // If no new keys, return prev to avoid re-render
        if (newKeys.length === 0) {
          return prev
        }

        // Only add the new keys, preserve all existing values
        const updated = { ...prev }
        newKeys.forEach(key => {
          updated[key] = combinedInitialVisibility[key]
        })
        return updated
      })
    }
  }, [
    kyb?._id, // Only re-run when KYB ID changes (modal opens with different KYB)
    entity,
    hasRelatedPersons,
    hasRelatedCompanies,
  ])

  const orderedCheckboxesConfig = useMemo(() => {
    const config = []

    // 1. Consultation Block - Always include checkbox
    config.push({
      key: 'consultationBlock',
      label: t('common:consultation'),
    })

    // 2. Executive Summary - Always include checkbox
    config.push({
      key: 'executiveSummarySection',
      label: t('common:executiveSummary'),
    })

    // 3. Company Details - Always include checkbox
    config.push({
      key: 'companyDetails',
      label: entity === 'BUSINESS' ? t('common:companyDetails') : t('common:details'),
    })

    // 4. Status Section - Nuevo checkbox
    if (kyb?.status) {
      config.push({
        key: 'statusSection',
        label: t('common:status'),
      })
    }

    // 5. Shareholders Business - MODIFICADO: Siempre visible
    if (entity === 'BUSINESS') {
      config.push({
        key: 'shareHoldersBusiness',
        label: `${t('common:shareholders')} (${t('common:business')})`,
      })

      // 6. Shareholders Persons - MODIFICADO: Siempre visible
      config.push({
        key: 'shareHoldersPersons',
        label: `${t('common:shareholders')} (${t('common:persons')})`,
      })
    }

    // 6. Manual Data
    if (entity === 'BUSINESS') {
      config.push({
        key: 'manualDataSection',
        label: t('common:manualData'),
      })
    }

    // 7. Risk Matrix Results - Include checkbox if data exists
    if (kyb?.riskResult || kyb?.riskScore) {
      config.push({
        key: 'riskMatrixResults',
        label: t('common:riskMatrixResults'),
      })
    }

    // 8. Dynamic checks from sections (in the order they appear in `sections`)
    sections.forEach((section) => {
      section.items.forEach((it) => {
        if (kyb?.[it.name]) {
          const fullLabel = t(`common:${titleMap[it.name]}`)
          // Use short label for AMLDD checks in checkboxes
          const label = KybUtils.isAmlddCheck(it.name) ? KybUtils.getShortCheckLabel(fullLabel) : fullLabel
          config.push({
            key: it.name,
            label: label,
          })
        }
      })
    })

    // 9. Related Persons
    if (dataRelatedPersons && dataRelatedPersons.length > 0) {
      config.push({
        key: 'relatedPersons',
        label: t('common:relatedPersons'),
      })
    }

    // 10. Related Companies
    if (dataRelatedCompanies && dataRelatedCompanies.length > 0) {
      config.push({
        key: 'relatedCompanies',
        label: t('common:relatedCompanies'),
      })
    }

    // 11. Metadata
    if (kyb?.metadata) {
      config.push({
        key: 'metadata',
        label: t('common:metadata'),
      })
    }

    // 12. Assigned Users
    if (kyb?.assignedUsers) {
      config.push({
        key: 'assignedUsers',
        label: t('common:assignedUsers'),
      })
    }

    // 13. Comments
    if (kyb?.comments) {
      config.push({
        key: 'comments',
        label: t('common:comments'),
      })
    }

    // 14. Timeline
    if (kyb?.timeline) {
      config.push({
        key: 'timeline',
        label: t('common:timeline'),
      })
    }

    return config
  }, [
    kyb,
    entity,
    sections,
    t,
    titleMap,
    dataShareHoldersPersons,
    dataShareHoldersBusiness,
    dataRelatedPersons,
    dataRelatedCompanies,
    currentCountry,
  ])

  const generatePDF = () => {
    setIsGeneratingPdf(true)
    const element = document.getElementById('contentToConvert')
    const options = {
      margin: [20, 15],
      filename: `${entity === 'BUSINESS' ? kyb?.companyDetails?.name : kyb?.name}_${today.getTime()}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: {
        scale: 1,
        letterRendering: true,
        useCORS: true,
        allowTaint: true,
        scrollY: -window.scrollY,
      },
      jsPDF: { unit: 'pt', format: 'letter', orientation: 'portrait' },
      pagebreak: { mode: ['avoid-all', 'css', 'legacy'] },
    }
    setTimeout(() => {
      html2pdf()
        .set(options)
        .from(element)
        .toPdf()
        .get('pdf')
        .then((pdf) => {
          const totalPages = pdf.internal.getNumberOfPages()
          const pageWidth = pdf.internal.pageSize.getWidth()
          const pageHeight = pdf.internal.pageSize.getHeight()

          for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i)
            pdf.setFontSize(10)
            pdf.setTextColor(150)
            pdf.text(`Güeno - Relatório gerado em ${buildTimeStringFromTimestamp(today)} por ${email}`, 10, 20)
            pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 80, pageHeight - 10)
          }
        })
        .save()
        .then(() => setIsGeneratingPdf(false))
    }, 100)
  }

  const scrollToTop = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const scrollToBottom = () => {
    if (contentRef.current) {
      contentRef.current.scrollTo({ top: contentRef.current.scrollHeight, behavior: 'smooth' })
    }
  }

  if (isLoading || isFetching) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Box className="modal-kyb-head">
        <Button
          onClick={generatePDF}
          variant="contained"
          size="small"
          color="secondary"
          disabled={isLoading || isFetching || isGeneratingPdf}
        >
          {t('common:exportToPdf')}
        </Button>
        <Fab variant="close" onClick={() => setOpen(false)} disabled={isLoading || isFetching || isGeneratingPdf}>
          <CloseOutlinedIcon />
        </Fab>
      </Box>

      <Box className="content-to-convert-container" ref={contentRef}>
        <Box className="detail-buttons">
          <Box className="detail-group-flow">
            <Box>
              {(() => {
                const visibleKeys = orderedCheckboxesConfig.map(({ key }) => key)
                const visibleStates = visibleKeys.map((key) => !!checksVisibility[key])
                const allChecked = visibleStates.every(Boolean)
                const someChecked = visibleStates.some(Boolean)
                return (
                  <FormControlLabel
                    control={<Checkbox checked={allChecked} indeterminate={someChecked && !allChecked} />}
                    label={<Typography variant="subtitle3">{t('common:showAll')}</Typography>}
                    onChange={(_, checked) => {
                      const newState = { ...checksVisibility }
                      visibleKeys.forEach((key) => {
                        newState[key] = checked
                      })
                      setChecksVisibility(newState)
                    }}
                  />
                )
              })()}
            </Box>
            <Divider sx={{ margin: '.1rem 0' }} />
            {orderedCheckboxesConfig.map(({ key, label }) => (
              <div key={key}>
                <FormControlLabel
                  control={<Checkbox checked={!!checksVisibility[key]} />}
                  label={<Typography variant="subtitle3">{label}</Typography>}
                  onChange={() => toggleVisibility(key)}
                />
                <Divider sx={{ margin: '.1rem 0' }} />
              </div>
            ))}
          </Box>
        </Box>

        <Box className="scroll-buttons no-print">
          <Button variant="contained" size="small" onClick={scrollToTop}>
            <ArrowUpwardIcon fontSize="small" />
          </Button>
          <Button variant="contained" size="small" onClick={scrollToBottom}>
            <ArrowDownwardOutlinedIcon fontSize="small" />
          </Button>
        </Box>

        <Box
          id="contentToConvert"
          className={`${theme === 'light' ? 'content-light' : 'content-dark'} content-to-convert`}
        >
          {isGeneratingPdf && (
            <Cover
              kyb={kyb}
              entity={entity}
              currentCountry={currentCountry}
              t={t}
              date={buildTimeStringFromTimestamp(today)}
              name={entity === 'BUSINESS' ? kyb?.companyDetails?.name : kyb?.name}
              documentNumber={
                currentCountry === 'BR'
                  ? kyb?.companyDetails?.cnpj
                  : currentCountry !== 'BR'
                  ? kyb?.companyDetails?.documentNumber
                  : '---'
              }
            />
          )}

          <Box style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '20px 1rem', width: '100%' }}>
            {/* CONSULTATION BLOCK */}
            {checksVisibility.consultationBlock && (
              <>
                <Typography variant="title" color="primary">
                  {t('common:consultation')}
                </Typography>
                <Typography variant="subtitle1">{t('common:consultationDesc')}</Typography>
                <Typography variant="subtitle4">{t('common:requesterData')}</Typography>
                <Paper elevation={0} style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <Typography variant="subtitle1">{t('common:user')}</Typography>
                    <Typography variant="subtitle3">{email}</Typography>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                    <Typography variant="subtitle1">{t('common:consultationDate')}</Typography>
                    <Typography variant="subtitle3">{buildTimeStringFromTimestamp(today)}</Typography>
                  </div>
                </Paper>
                <Consultations consultations={checksVisibility} titleMap={titleMap} />
              </>
            )}

            {/* EXECUTIVE SUMMARY */}
            {checksVisibility.executiveSummarySection && (
              <ExecutiveSummary
                riskHistory={kyb?.riskHistory}
                riskLevel={kyb?.riskLevel}
                name={entity === 'BUSINESS' ? kyb?.companyDetails?.name : kyb?.name}
              />
            )}

            {/* COMPANY DETAILS */}
            {checksVisibility.companyDetails && (
              <CompanyDetails currentCountry={currentCountry} kyb={kyb} entity={entity} />
            )}

            {/* STATUS SECTION */}
            {checksVisibility.statusSection && kyb?.status && <CheckStatus status={kyb.status} />}

            {/* MANUAL DATA */}
            {checksVisibility.manualDataSection && <ManualInfo kyb={kyb} />}

            {/* RISK MATRIX RESULTS */}
            {checksVisibility.riskMatrixResults && (
              <RiskMatrixResults riskResult={kyb?.riskResult} riskScore={kyb?.riskScore} />
            )}

            {sections.map((section) => {
              const visibleItems = section.items
                ? section.items.filter((it) => {
                    const Component = checkComponentMap[it.name]
                    return Component && checksVisibility[it.name] && checksData[it.name]
                  })
                : []

              if (visibleItems.length > 0) {
                return (
                  <Box key={section.title} sx={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <Typography variant="title" color="primary">
                      {t(`messages:kyb.checks.title.${section.title}`)}
                    </Typography>
                    <Typography variant="subtitle1">{t(`messages:kyb.checks.subtitle.${section.subtitle}`)}</Typography>
                    {visibleItems.map((it) => {
                      const Component = checkComponentMap[it.name]
                      const propName = checkPropMap[it.name] || `${it.name}Check`
                      const props = { [propName]: checksData[it.name] }

                      // Add type prop for AMLDD checks
                      if (KybUtils.isAmlddCheck(it.name)) {
                        const typeMap = {
                          amlddPep: 'PEP',
                          amlddEsg: 'ESG',
                          amlddLri: 'LRI',
                          amlddMtlsn: 'MTLSN',
                          amlddMtlsnSimple: 'MTLSN_SIMPLE',
                          amlddMtlsnWithNews: 'MTLSN_NEWS',
                        }
                        props.type = typeMap[it.name]
                      }

                      return <Component key={it.name} {...props} />
                    })}
                    <Divider sx={{ my: 2 }} />
                  </Box>
                )
              }
              return null
            })}

            {/* RELATIONS */}
            {checksVisibility.shareHoldersPersons && (
              <CheckShareholders
                shareholdersCheck={dataShareHoldersPersons}
                name={`${t('common:shareholders')} (${t('common:persons')})`}
              />
            )}
            {checksVisibility.shareHoldersBusiness && (
              <CheckBusinessShareholders
                shareholdersCheck={adaptedShareHoldersBusiness}
                currentCountry={currentCountry}
                name={`${t('common:shareholders')} (${t('common:business')})`}
              />
            )}
            {checksVisibility.relatedPersons && (
              <CheckShareholders shareholdersCheck={dataRelatedPersons} name={t(`common:${'relatedPersons'}`)} />
            )}
            {checksVisibility.relatedCompanies && (
              <CheckBusinessShareholders
                shareholdersCheck={dataRelatedCompanies}
                currentCountry={currentCountry}
                name={t(`common:${'relatedCompanies'}`)}
              />
            )}
            {checksVisibility.metadata && (
              <>
                <Typography variant="title" color="primary">
                  {t('common:metadata')}
                </Typography>
                <Paper elevation={0} style={{ display: 'flex', flexDirection: 'column', gap: '.75rem' }}>
                  {kyb?.metadata &&
                    Object.keys(kyb.metadata)?.map((key) => (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                        <Typography variant="subtitle1">{key}</Typography>
                        <Typography variant="subtitle3">{kyb.metadata[key]}</Typography>
                      </div>
                    ))}
                </Paper>
              </>
            )}
            {checksVisibility.assignedUsers && <CheckAssignedUsers assignedUsersCheck={kyb?.assignedUsers} />}
            {checksVisibility.comments && <CheckComments commentsCheck={kyb?.comments} />}
            {checksVisibility.timeline && <CheckTimeline timelineCheck={kyb?.timeline} />}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export default KybToPdf
